if (typeof HeroCarousel === 'undefined') {
    class HeroCarousel extends HTMLElement {
        connectedCallback() {
            this.slideContentHeight = 0;
            this.handleResize = this.handleResize.bind(this);

            const swiperContainer = this.querySelector('.swiper');
            if (!swiperContainer || this.swiper) return;

            const autoplayEnabled = this.dataset.autoplay === 'true';
            const autoplayDelay = parseInt(this.dataset.autoplayDelay, 10) || 5000;

            const autoplayConfig = autoplayEnabled ? {
                delay: autoplayDelay,
                disableOnInteraction: true,
                pauseOnMouseEnter: false
            } : false;

            whenReady('Swiper', () => {
                try {
                    this.swiper = new Swiper(swiperContainer, {
                        loop: true,
                        a11y: true,
                        autoHeight: true,
                        autoplay: autoplayConfig,
                        modules: [Swiper.EffectFade, Swiper.Autoplay, Swiper.A11y, Swiper.Pagination],
                        pagination: {
                            el: this.querySelector('.swiper-pagination'),
                            type: 'bullets',
                            clickable: true
                        }
                    });

                    this.updateSlideContentHeight(); // Initial setup
                    window.addEventListener('resize', this.handleResize);

                    this.swiper.on('slideChange', () => {
                        if(this.swiper.slides[this.swiper.activeIndex].querySelector("video") !== null){
                            const video = this.swiper.slides[this.swiper.activeIndex].querySelector("video");
                            if (video) {
                                // Preserve muted state if user has previously unmuted this video
                                // Only set to muted if it hasn't been explicitly unmuted by user
                                const wasUnmuted = video.dataset.userUnmuted === 'true';
                                if (!wasUnmuted) {
                                    video.muted = true; // Muted videos have better autoplay support
                                }
                                
                                video.playsInline = true; // Prevents fullscreen on iOS
                                
                                // Try to play with a small delay to ensure DOM is ready
                                setTimeout(() => {
                                    const playPromise = video.play();
                                    if (playPromise !== undefined) {
                                        playPromise.catch(error => {
                                            // Autoplay was prevented or failed
                                            console.log('Video autoplay prevented:', error);
                                            
                                            // Try one more time with user interaction simulation
                                            // This is a last resort and may not work on all devices
                                            try {
                                                video.currentTime = 0;
                                                video.play();
                                            } catch (retryError) {
                                                console.log('Video autoplay retry failed:', retryError);
                                            }
                                        });
                                    }
                                }, 100);
                            }
                        }
                    });

                    if (this.querySelectorAll(".swiper-next-button").length) {
                        this.querySelectorAll(".swiper-next-button").forEach($button => {
                            $button.addEventListener("click", e => {
                                e.preventDefault();
                                this.swiper.slideNext();
                            });
                        });
                    }
                } catch (error) {
                    console.error('Failed to initialize Swiper:', error);
                }
            });
        }

        disconnectedCallback() {
            if (this.swiper) {
                this.swiper.destroy(true, true);
                this.swiper = null;
            }
            window.removeEventListener('resize', this.handleResize);
        }

        updateSlideContentHeight() {
            
            this.slideContentHeight = 0;
            const slides = this.querySelectorAll(".hero-v2__content > div");

            slides.forEach($slide => {
                $slide.style.height = 'auto'; // Reset before measuring
                if ($slide.clientHeight > this.slideContentHeight) {
                    this.slideContentHeight = $slide.clientHeight;
                }
            });

            slides.forEach($slide => {
                $slide.style.height = `${this.slideContentHeight}px`;
            });

        }

        handleResize() {
            clearTimeout(this.resizeTimeout);
            this.resizeTimeout = setTimeout(() => {
                this.updateSlideContentHeight();
            }, 150);
        }
    }

    if (!window.customElements.get('hero-carousel')) {
        window.customElements.define('hero-carousel', HeroCarousel);
    }
}

if (typeof HeroVideoToggle === 'undefined') {
    class HeroVideoToggle extends HTMLElement {
        constructor() {
            super();
            this.handlePlayClick = this.handlePlayClick.bind(this);
            this.handlePauseClick = this.handlePauseClick.bind(this);
            this.handleVideoPlay = this.handleVideoPlay.bind(this);
            this.handleVideoPause = this.handleVideoPause.bind(this);
            this.handleContainerClick = this.handleContainerClick.bind(this);
            this.handleKeydown = this.handleKeydown.bind(this);
            this.togglePlayback = this.togglePlayback.bind(this);
        }

        connectedCallback() {
            this.video = this.findVideoElement();
            if (!this.video) {
                console.warn('HeroVideoToggle: No video element found in this slide.');
                return;
            }

            this.setAttribute('role', 'group');
            this.setAttribute('aria-label', 'Video playback controls');

            const buttons = Array.from(this.querySelectorAll('button'));
            this.playButton = buttons[0] || this.createFallbackButton('Play');
            this.pauseButton = buttons[1] || this.createFallbackButton('Pause Video');

            [this.playButton, this.pauseButton].forEach(button => {
                if (!button.hasAttribute('type')) {
                    button.type = 'button';
                }
            });

            this.playButton.addEventListener('click', this.handlePlayClick);
            this.pauseButton.addEventListener('click', this.handlePauseClick);
            this.video.addEventListener('play', this.handleVideoPlay);
            this.video.addEventListener('pause', this.handleVideoPause);

            this.setPlayingState(!this.video.paused);

            this.addEventListener('click', this.handleContainerClick);
            this.addEventListener('keydown', this.handleKeydown);

            if (!this.hasAttribute('tabindex')) {
                this.setAttribute('tabindex', '0');
            }
        }

        disconnectedCallback() {
            if (this.playButton) {
                this.playButton.removeEventListener('click', this.handlePlayClick);
            }
            if (this.pauseButton) {
                this.pauseButton.removeEventListener('click', this.handlePauseClick);
            }
            if (this.video) {
                this.video.removeEventListener('play', this.handleVideoPlay);
                this.video.removeEventListener('pause', this.handleVideoPause);
            }
            this.removeEventListener('click', this.handleContainerClick);
            this.removeEventListener('keydown', this.handleKeydown);
        }

        findVideoElement() {
            let current = this.parentElement;
            while (current && !current.classList.contains('swiper-slide')) {
                current = current.parentElement;
            }
            if (current) {
                const video = current.querySelector('video.hero__asset') || current.querySelector('video');
                if (video) return video;
            }
            const heroCarousel = this.closest('hero-carousel');
            if (heroCarousel) {
                const activeSlide = heroCarousel.querySelector('.swiper-slide-active');
                if (activeSlide) {
                    const video = activeSlide.querySelector('video');
                    if (video) return video;
                }
            }
            return document.querySelector('video.hero__asset');
        }

        createFallbackButton(label) {
            const button = document.createElement('button');
            button.type = 'button';
            button.textContent = label;
            this.appendChild(button);
            return button;
        }

        handlePlayClick(event) {
            event.preventDefault();
            event.stopPropagation();
            this.play();
        }

        handlePauseClick(event) {
            event.preventDefault();
            event.stopPropagation();
            this.pause();
        }

        handleContainerClick(event) {
            if (event.target.closest('button')) {
                return;
            }
            this.togglePlayback();
        }

        handleKeydown(event) {
            if (event.target !== this) return;
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                this.togglePlayback();
            }
        }

        handleVideoPlay() {
            this.setPlayingState(true);
        }

        handleVideoPause() {
            this.setPlayingState(false);
        }

        play() {
            if (!this.video) return;
            const playPromise = this.video.play();
            if (playPromise && typeof playPromise.catch === 'function') {
                playPromise.catch(error => {
                    console.warn('HeroVideoToggle: play failed.', error);
                    this.setPlayingState(false);
                });
            }
        }

        pause() {
            if (this.video) {
                this.video.pause();
            }
        }

        togglePlayback() {
            if (this.video.paused) {
                this.play();
            } else {
                this.pause();
            }
        }

        setPlayingState(isPlaying) {
            this.setButtonVisibility(this.playButton, !isPlaying);
            this.setButtonVisibility(this.pauseButton, isPlaying);
            this.toggleAttribute('data-playing', isPlaying);
        }

        setButtonVisibility(button, shouldShow) {
            if (!button) return;
            button.style.display = shouldShow ? '' : 'none';
            button.hidden = !shouldShow;
            button.setAttribute('aria-hidden', String(!shouldShow));
        }
    }

    if (!window.customElements.get('hero-video-toggle')) {
        window.customElements.define('hero-video-toggle', HeroVideoToggle);
    }
}

if (typeof HeroAudio === 'undefined') {
    class HeroAudio extends HTMLElement {
        constructor() {
            super();

            this.isLoaded = false;
            this.isPlaying = false;
            this.audio = null;
            this.observer = null;

            this.handlePlayClick = this.handlePlayClick.bind(this);
            this.handlePauseClick = this.handlePauseClick.bind(this);
            this.handleAudioPlay = this.handleAudioPlay.bind(this);
            this.handleAudioPause = this.handleAudioPause.bind(this);
            this.handleAudioEnded = this.handleAudioEnded.bind(this);
            this.handleIntersection = this.handleIntersection.bind(this);
            this.handleContainerClick = this.handleContainerClick.bind(this);
            this.handleKeydown = this.handleKeydown.bind(this);
            this.togglePlayback = this.togglePlayback.bind(this);
        }

        connectedCallback() {
            this.mp3 = this.getAttribute('mp3');
            if (!this.mp3) {
                console.warn('HeroAudio: missing `mp3` attribute.');
                return;
            }

            this.classList.add('hero-audio');
            this.setAttribute('role', 'group');

            const buttons = Array.from(this.querySelectorAll('button'));
            this.playButton = buttons[0] || this.createFallbackButton('Play');
            this.pauseButton = buttons[1] || this.createFallbackButton('Pause');

            [this.playButton, this.pauseButton].forEach(button => {
                if (!button.hasAttribute('type')) {
                    button.type = 'button';
                }
                button.classList.add('hero-audio__button');
            });

            this.playButton.addEventListener('click', this.handlePlayClick);
            this.pauseButton.addEventListener('click', this.handlePauseClick);

            this.setPlayingState(false);

            this.addEventListener('click', this.handleContainerClick);
            this.addEventListener('keydown', this.handleKeydown);

            if (!this.hasAttribute('tabindex')) {
                this.setAttribute('tabindex', '0');
            }

            if ('IntersectionObserver' in window) {
                this.observer = new IntersectionObserver(this.handleIntersection, {
                    root: null,
                    threshold: 0.25
                });
                this.observer.observe(this);
            } else {
                this.loadAudio();
            }
        }

        disconnectedCallback() {
            this.detachObserver();
            this.teardownAudio();

            if (this.playButton) {
                this.playButton.removeEventListener('click', this.handlePlayClick);
            }
            if (this.pauseButton) {
                this.pauseButton.removeEventListener('click', this.handlePauseClick);
            }
            this.removeEventListener('click', this.handleContainerClick);
            this.removeEventListener('keydown', this.handleKeydown);
        }

        static get observedAttributes() {
            return ['mp3'];
        }

        attributeChangedCallback(name, _oldValue, newValue) {
            if (name === 'mp3' && newValue !== this.mp3) {
                this.mp3 = newValue;
                if (this.isLoaded) {
                    this.resetAudio();
                }
            }
        }

        createFallbackButton(label) {
            const button = document.createElement('button');
            button.type = 'button';
            button.textContent = label;
            this.appendChild(button);
            return button;
        }

        handleIntersection(entries) {
            entries.forEach(entry => {
                if (entry.isIntersecting) {
                    this.loadAudio();
                    if (this.observer) {
                        this.observer.disconnect();
                        this.observer = null;
                    }
                }
            });
        }

        loadAudio() {
            if (this.isLoaded || !this.mp3) {
                return;
            }

            this.audio = new Audio();
            this.audio.src = this.mp3;
            this.audio.preload = 'auto';
            this.audio.loop = this.hasAttribute('loop');

            this.audio.addEventListener('play', this.handleAudioPlay);
            this.audio.addEventListener('pause', this.handleAudioPause);
            this.audio.addEventListener('ended', this.handleAudioEnded);

            this.isLoaded = true;
            this.setAttribute('data-audio-loaded', 'true');
        }

        handlePlayClick(event) {
            event.preventDefault();
            event.stopPropagation();
            this.play();
        }

        handlePauseClick(event) {
            event.preventDefault();
            event.stopPropagation();
            this.pause();
        }

        handleContainerClick(event) {
            if (event.target.closest('button')) {
                return;
            }
            this.togglePlayback();
        }

        handleKeydown(event) {
            if (event.target !== this) {
                return;
            }
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                this.togglePlayback();
            }
        }

        play() {
            this.loadAudio();
            if (!this.audio) {
                return;
            }

            document.querySelectorAll('hero-audio').forEach(instance => {
                if (instance !== this && typeof instance.pause === 'function') {
                    instance.pause();
                }
            });

            const playPromise = this.audio.play();
            if (playPromise && typeof playPromise.catch === 'function') {
                playPromise.catch(error => {
                    console.warn('HeroAudio: playback failed.', error);
                    this.setPlayingState(false);
                });
            }
        }

        pause() {
            if (this.audio) {
                this.audio.pause();
            }
        }

        togglePlayback() {
            if (this.isPlaying) {
                this.pause();
            } else {
                this.play();
            }
        }
        handleAudioPlay() {
            this.setPlayingState(true);
        }

        handleAudioPause() {
            this.setPlayingState(false);
        }

        handleAudioEnded() {
            if (this.audio) {
                this.audio.currentTime = 0;
            }
            this.setPlayingState(false);
        }

        setPlayingState(isPlaying) {
            this.isPlaying = isPlaying;
            this.setButtonVisibility(this.playButton, !isPlaying);
            this.setButtonVisibility(this.pauseButton, isPlaying);
            this.toggleAttribute('data-playing', isPlaying);
        }

        setButtonVisibility(button, shouldShow) {
            if (!button) {
                return;
            }
            button.style.display = shouldShow ? 'block' : 'none';
            button.setAttribute('aria-hidden', String(!shouldShow));
        }

        resetAudio() {
            this.teardownAudio();
            this.isLoaded = false;
            this.setPlayingState(false);
            this.removeAttribute('data-audio-loaded');
            this.loadAudio();
        }

        teardownAudio() {
            if (!this.audio) {
                return;
            }
            this.audio.pause();
            this.audio.removeEventListener('play', this.handleAudioPlay);
            this.audio.removeEventListener('pause', this.handleAudioPause);
            this.audio.removeEventListener('ended', this.handleAudioEnded);
            this.audio = null;
        }

        detachObserver() {
            if (this.observer) {
                this.observer.disconnect();
                this.observer = null;
            }
        }
    }

    if (!window.customElements.get('hero-audio')) {
        window.customElements.define('hero-audio', HeroAudio);
    }
}
